import React, { useEffect, useState } from "react";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Slider from "@mui/material/Slider";
import Box from "@mui/material/Box";

import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";

const iOSBoxShadow =
  "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.13),0 0 0 1px rgba(0,0,0,0.02)";

const IOSSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#3880ff" : "#3880ff",
  height: 2,
  padding: "15px 0",
  "& .MuiSlider-thumb": {
    height: 14,
    width: 14,
    backgroundColor: "#fff",
    boxShadow: iOSBoxShadow,
    "&:focus, &:hover, &.Mui-active": {
      boxShadow:
        "0 3px 1px rgba(0,0,0,0.1),0 4px 8px rgba(0,0,0,0.3),0 0 0 1px rgba(0,0,0,0.02)",
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        boxShadow: iOSBoxShadow,
      },
    },
  },
  "& .MuiSlider-valueLabel": {
    fontSize: 12,
    fontWeight: "normal",
    top: -6,
    backgroundColor: "unset",
    color: theme.palette.text.primary,
    "&:before": {
      display: "none",
    },
    "& *": {
      background: "transparent",
      color: theme.palette.mode === "dark" ? "#fff" : "#000",
    },
  },
  "& .MuiSlider-track": {
    border: "none",
  },
  "& .MuiSlider-rail": {
    opacity: 0.5,
    backgroundColor: "#bfbfbf",
  },
  "& .MuiSlider-mark": {
    backgroundColor: "#bfbfbf",
    height: 8,
    width: 1,
    "&.MuiSlider-markActive": {
      opacity: 1,
      backgroundColor: "currentColor",
    },
  },
}));

function ConditionalSlider(props) {
  const { handleConditions, initInterval, boundaries, handleFormValidation } =
    props;
  const { t } = useTranslation();
  const logics = [
    { value: "smaller", title: t("editor.conditionalDialog.smaller") },
    { value: "greater", title: t("editor.conditionalDialog.greater") },
    { value: "between", title: t("editor.conditionalDialog.between") },
  ];
  const [logic, setLogic] = useState("between"); // "smaller", "greater", "between"
  const [interval, setInterval] = useState(
    JSON.parse(JSON.stringify(initInterval))
  ); // deep clone the interval (array of objects)

  const handleIntervalChange = (pos) => (event) => {
    const value = event.target.value;
    setInterval((prevInterval) => {
      let newInterval = [...prevInterval];
      newInterval[pos].value = value;
      return newInterval;
    });
  };

  const handleLogicChange = (event) => {
    switch (event.target.value) {
      case "smaller":
        handleIntervalChange(0)({ target: { value: boundaries[0].value } }); // set value of left mark
        break;
      case "greater":
        handleIntervalChange(1)({ target: { value: boundaries[1].value } }); // set value of right mark
        break;
      default:
        break;
    }
    setLogic(event.target.value); // set mode (smaller/greater/between)
  };

  // interval validation
  const getValidatedValueByPosition = (pos) =>
    Number(interval[pos].value) >= Number(boundaries[0].value) &&
    Number(interval[pos].value) <= Number(boundaries[1].value);

  useEffect(() => {
    // submit
    const getMarks = () => {
      const leftValue = Number(interval[0].value);
      const rightValue = Number(interval[1].value);
      const leftMark = { ...interval[0], value: leftValue };
      const rightMark = { ...interval[1], value: rightValue };
      return [leftMark, rightMark];
    };
    handleConditions(getMarks());
    handleFormValidation(
      getValidatedValueByPosition(0) && getValidatedValueByPosition(1)
    );
  }, [interval]);

  const renderTextField = (pos) => {
    return (
      <TextField
        label=""
        type="number"
        error={!getValidatedValueByPosition(pos)}
        value={interval[pos].value + ""}
        sx={{ maxWidth: 60, textAlignLast: "center" }}
        onChange={handleIntervalChange(pos)}
        InputLabelProps={{
          shrink: true,
        }}
        variant="standard"
      />
    );
  };

  const renderInputFields = () => {
    switch (logic) {
      case "between":
        return (
          <React.Fragment>
            {renderTextField(0)}
            <div style={{ display: "inline-flex", verticalAlign: "sub" }}>
              {t("editor.conditionalDialog.and")}
            </div>
            {renderTextField(1)}
          </React.Fragment>
        );
      case "greater":
        return renderTextField(0);

      case "smaller":
        return renderTextField(1);

      default:
        return <div></div>;
    }
  };

  return (
    <React.Fragment>
      <FormControl>
        <Select
          value={logic}
          onChange={handleLogicChange}
          displayEmpty
          inputProps={{ "aria-label": "slider rule" }}
          variant="standard"
        >
          {logics.map((item) => (
            <MenuItem key={"MenuItem" + item.value} value={item.value}>
              {item.title}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      {renderInputFields()}
      <Box paddingTop={4} paddingLeft={1} paddingRight={1}>
        <IOSSlider
          value={[Number(interval[0].value), Number(interval[1].value)]}
          onChange={(_, newValue) => {
            setInterval((prev) => [
              { ...prev[0], value: newValue[0] },
              { ...prev[1], value: newValue[1] },
            ]);
            setLogic("between");
          }}
          aria-labelledby="range-slider"
          min={Number(boundaries[0].value)}
          max={Number(boundaries[1].value)}
          valueLabelDisplay="on"
        />
      </Box>
    </React.Fragment>
  );
}

export default ConditionalSlider;
